import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Card,
  CardContent,
  useTheme,
  Stack,
  alpha,
  LinearProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import NatureIcon from '@mui/icons-material/Nature';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HandymanIcon from '@mui/icons-material/Handyman';
import { firebaseAuth } from '../../shared/services/firebase.service';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  // Firebase restores the persisted session asynchronously. Until it resolves we
  // don't know whether to redirect a logged-in user to the app, so show a loading
  // bar instead of flashing the marketing page.
  const [authResolved, setAuthResolved] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        navigate('/home', { replace: true });
        return;
      }
      setAuthResolved(true);
    });
    return unsubscribe;
  }, [navigate]);

  if (!authResolved) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.12)} 0%, ${theme.palette.background.default} 55%, ${theme.palette.grey[100]} 100%)`,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <LinearProgress />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <NatureIcon sx={{ fontSize: 56, color: theme.palette.primary.main }} />
          <Typography variant="h5" fontWeight="bold">
            SavaQuan
          </Typography>
        </Box>
      </Box>
    );
  }

  const handleLogin = () => {
    navigate('/login');
  };

  const handlePrivacyPolicy = () => {
    navigate('/privacy-policy');
  };

  const handleTermsOfService = () => {
    navigate('/terms-of-service');
  };

  const features = [
    {
      icon: <AssessmentIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Professional Estimates',
      description: 'Create detailed, professional estimates for tree services with ease.'
    },
    {
      icon: <CalendarTodayIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Appointment Management',
      description: 'Schedule and manage appointments with integrated mapping and routing.'
    },
    {
      icon: <HandymanIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Job Tracking',
      description: 'Track jobs from estimate to completion with real-time updates.'
    }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.12)} 0%, ${theme.palette.background.default} 55%, ${theme.palette.grey[100]} 100%)`,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4, pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <NatureIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              SavaQuan
            </Typography>
          </Box>
          <Button 
            variant="outlined" 
            onClick={handleLogin}
            sx={{ minWidth: 100 }}
          >
            Login
          </Button>
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', alignItems: 'center', py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={4}>
              <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
                Professional Tree Service Management
              </Typography>
              <Typography variant="h6" color="text.secondary" lineHeight={1.6}>
                Streamline your tree service business with our comprehensive management platform. 
                Create estimates, manage appointments, track jobs, and grow your business efficiently.
              </Typography>
              <Box>
                <Button 
                  variant="contained" 
                  size="large" 
                  onClick={handleLogin}
                  sx={{ 
                    mr: 2, 
                    px: 4, 
                    py: 1.5,
                    fontSize: '1.1rem'
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={8}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                background: `linear-gradient(45deg, ${theme.palette.primary.light} 30%, ${theme.palette.primary.main} 90%)`,
                color: 'white'
              }}
            >
              <NatureIcon sx={{ fontSize: 80, mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                SavaQuan Platform
              </Typography>
              <Typography variant="body1">
                Your complete tree service management solution
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight="bold">
          Why Choose Our Platform?
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Everything you need to manage and grow your tree service business
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card 
                elevation={4}
                sx={{ 
                  height: '100%', 
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Box sx={{ mb: 3 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: 4, 
          borderTop: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2}>
                <NatureIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="body2" color="text.secondary">
                  © 2025 SavaQuan. All rights reserved.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" gap={3}>
                <Button 
                  color="inherit" 
                  onClick={handlePrivacyPolicy}
                  sx={{ textTransform: 'none' }}
                >
                  Privacy Policy
                </Button>
                <Button 
                  color="inherit" 
                  onClick={handleTermsOfService}
                  sx={{ textTransform: 'none' }}
                >
                  Terms of Service
                </Button>
                <Button
                  color="inherit"
                  onClick={() => navigate('/support')}
                  sx={{ textTransform: 'none' }}
                >
                  Support
                </Button>
                <Button 
                  color="inherit" 
                  onClick={handleLogin}
                  sx={{ textTransform: 'none' }}
                >
                  Login
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage; 